/**
 * @file EconomyPresenter.ts
 * @role Presents balance + bet to the UI. The balance is NEVER owned or computed here —
 *       it is set from the validated session and, after each spin, from the validated
 *       result's balanceAfterMinor. Subscribers (the HUD) are notified on change.
 * @owns The displayed balance/bet state + change notifications.
 * @reads The validated session balance + SpinResultDto.balanceAfterMinor.
 * @neverDoes Mutate balance on its own (Invariant 4). Compute wins (Invariant 2). Use floats.
 * @invariants 2, 4, 7.
 * @see docs/GAME_FLOW.md
 */
import type { Money } from '@core/money/Money';
import type { SpinResultDto } from '@core/backend/dto';

export interface EconomyState {
  balance: Money;
  bet: Money;
}

type EconomyListener = (state: EconomyState) => void;

export class EconomyPresenter {
  private balanceMinor: number;
  private betMinor: number;
  private readonly listeners = new Set<EconomyListener>();

  constructor(
    private readonly currency: string,
    initialBalanceMinor: number,
    betMinor: number,
  ) {
    this.balanceMinor = initialBalanceMinor;
    this.betMinor = betMinor;
  }

  get state(): EconomyState {
    return {
      balance: { amountMinor: this.balanceMinor, currency: this.currency },
      bet: { amountMinor: this.betMinor, currency: this.currency },
    };
  }

  /** Adopt the authoritative balance from a validated spin result (Invariant 4/5). */
  applyResult(result: SpinResultDto): void {
    this.balanceMinor = result.balanceAfterMinor;
    this.notify();
  }

  setBet(betMinor: number): void {
    this.betMinor = betMinor;
    this.notify();
  }

  /** Subscribe; the listener is called immediately with the current state. Returns unsubscribe. */
  subscribe(listener: EconomyListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of [...this.listeners]) {
      listener(this.state);
    }
  }
}
